import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Clock, ArrowLeft, Rocket, ShieldCheck, CalendarCheck, CheckCircle2 } from 'lucide-react';

const Registro = () => {
  useEffect(() => { document.title = 'Prueba gratis 15 días — Control Horario | GestionaEmpleados'; }, []);
  const [form, setForm] = useState({
    empresa_nombre: '', cif: '', email: '', telefono: '',
    admin_full_name: '', admin_username: '', admin_password: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`¡Bienvenido, ${u.full_name}! Tu prueba de ${u.dias_prueba_restantes} días ha comenzado.`);
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se ha podido completar el registro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4" data-testid="registro-page">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-6 items-stretch">
        <Card className="border-zinc-200 shadow-lg bg-zinc-900 text-white hidden md:flex">
          <CardContent className="p-8 flex flex-col justify-center space-y-6">
            <div className="bg-white/10 p-3 rounded-2xl w-fit"><Clock className="h-7 w-7 text-white" /></div>
            <div>
              <h2 className="font-heading text-3xl font-bold leading-tight">Prueba GestionaEmpleado gratis</h2>
              <p className="text-zinc-300 mt-3">Sin tarjeta. Crea tu cuenta y empieza a fichar en minutos.</p>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Fichaje en tablet por PIN</li>
              <li className="flex items-center gap-3"><CalendarCheck className="h-5 w-5 text-emerald-400" /> Calendario laboral por centro</li>
              <li className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-emerald-400" /> Informes legales PDF/Excel</li>
              <li className="flex items-center gap-3"><Rocket className="h-5 w-5 text-emerald-400" /> Hasta 10 empleados durante la prueba</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl font-bold text-zinc-900">Crea tu cuenta</CardTitle>
            <p className="text-zinc-500 text-sm">Empieza tu prueba gratuita, sin compromiso.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nombre de la empresa *</Label>
                <Input data-testid="reg-empresa" value={form.empresa_nombre} onChange={set('empresa_nombre')} required placeholder="Mi Empresa SL" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>CIF / NIF</Label>
                  <Input data-testid="reg-cif" value={form.cif} onChange={set('cif')} placeholder="B12345678" />
                </div>
                <div className="space-y-1.5">
                  <Label>Teléfono</Label>
                  <Input data-testid="reg-telefono" value={form.telefono} onChange={set('telefono')} placeholder="600 000 000" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email de contacto *</Label>
                <Input type="email" data-testid="reg-email" value={form.email} onChange={set('email')} required placeholder="tu@empresa.es" />
              </div>
              <div className="pt-2 border-t border-zinc-100">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Tu usuario administrador</p>
              </div>
              <div className="space-y-1.5">
                <Label>Nombre completo *</Label>
                <Input data-testid="reg-fullname" value={form.admin_full_name} onChange={set('admin_full_name')} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Usuario *</Label>
                  <Input data-testid="reg-username" value={form.admin_username} onChange={set('admin_username')} required autoComplete="username" />
                </div>
                <div className="space-y-1.5">
                  <Label>Contraseña *</Label>
                  <Input type="password" data-testid="reg-password" value={form.admin_password} onChange={set('admin_password')} required autoComplete="new-password" placeholder="mín. 6 caracteres" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="reg-submit-btn">
                {loading ? 'Creando tu cuenta...' : 'Empezar prueba gratis'}
              </Button>
            </form>
            <div className="flex items-center justify-between mt-5 text-sm">
              <Link to="/admin/login" className="text-zinc-600 hover:text-zinc-900">¿Ya tienes cuenta? Inicia sesión</Link>
              <Link to="/" className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-900">
                <ArrowLeft className="h-4 w-4" /> Kiosko
              </Link>
            </div>
            <div className="mt-6 pt-5 border-t border-zinc-100 text-center space-y-1.5">
              <p className="text-xs text-zinc-500 font-medium">¿Necesitas ayuda antes de empezar?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
                <a href="tel:+34680292427" className="text-zinc-900 font-semibold hover:text-zinc-600" data-testid="reg-phone">📞 680 292 427</a>
                <a href="mailto:comercial@gestionaempleados.com" className="text-zinc-900 font-semibold hover:text-zinc-600 break-all" data-testid="reg-email">✉ comercial@gestionaempleados.com</a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Registro;
